import { toValue } from "vue";
const TABBABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable]';
const isTabbable = (element) => !element.hasAttribute("disabled") && element.getAttribute("aria-hidden") !== "true" && element.tabIndex >= 0;
// jsdom has no layout, so an unsupported checkVisibility means "assume rendered" rather than "hidden".
const isRendered = (element) => typeof element.checkVisibility === "function" ? element.checkVisibility() : true;
export const getTabbableElements = (element) => {
    const root = toValue(element);
    if (!root || !(root instanceof HTMLElement)) {
        console.warn("getTabbableElements: invalid element", root);
        return [];
    }
    return Array.from(root.querySelectorAll(TABBABLE_SELECTOR)).filter((el) => {
        if (!isTabbable(el)) {
            return false;
        }
        // Traverse up to `root`, stopping early if a fixed/absolute container is found
        let current = el;
        while (current && current !== root) {
            const style = window.getComputedStyle(current);
            if (style.position === "fixed") {
                return false;
            }
            current = current.parentElement;
        }
        return true;
    });
};
// Keyboard navigation
export const focusSiblingElement = (navigationScope, // Array of elements to navigate through
activeElement, step) => {
    const scope = toValue(navigationScope);
    const active = toValue(activeElement);
    const elements = scope.flatMap(getTabbableElements);
    if (!scope.length || !active)
        return;
    // Get the index of the currently focused element
    const currentIndex = elements.indexOf(active);
    // helper function to focus an element by index
    const focusElement = (index) => {
        const element = elements[index];
        if (element) {
            element.focus();
        }
    };
    // Calculate the next index using modular arithmetic to wrap around the array
    const index = (currentIndex + step + elements.length) % elements.length;
    focusElement(index);
};
export const focusNextElement = (navigationScope, // Array of elements to navigate through
activeElement) => focusSiblingElement(navigationScope, activeElement, 1);
export const focusPreviousElement = (navigationScope, // Array of elements to navigate through
activeElement) => focusSiblingElement(navigationScope, activeElement, -1);
// Reproduces the Tab/Shift+Tab step from `reference` for handlers that must cancel the default action.
export const focusAdjacentTabbable = (reference, step, excludedSubtree) => {
    const anchor = toValue(reference);
    if (!anchor)
        return false;
    const candidates = Array.from(document.querySelectorAll(TABBABLE_SELECTOR));
    const anchorIndex = candidates.indexOf(anchor);
    if (anchorIndex < 0)
        return false;
    // Walked rather than filtered: pages carry thousands of candidates and visibility checks force layout.
    for (let index = anchorIndex + step; index >= 0 && index < candidates.length; index += step) {
        const candidate = candidates[index];
        if (excludedSubtree?.contains(candidate))
            continue;
        if (!isTabbable(candidate) || !isRendered(candidate))
            continue;
        candidate.focus();
        return true;
    }
    return false;
};
